package components.project.configuration.layout.moduleLayout.planning

import com.meistercharts.charts.pv.roofPlanning.PvRoofPlanningLayer
import com.meistercharts.charts.pv.roofPlanning.PvRoofPlanningModel
import com.meistercharts.charts.pv.stringsPlanning.PvStringsPlanningGestalt
import com.meistercharts.charts.pv.stringsPlanning.PvStringsPlanningModel
import com.meistercharts.react.meistercharts
import it.neckar.commons.kotlin.js.getNotNull
import it.neckar.commons.kotlin.js.safeGet
import it.neckar.lizergy.model.configuration.moduleLayout.ResolvedModuleLayout
import it.neckar.lizergy.model.configuration.moduleLayout.planning.PvRoofPlanningModelInformation
import it.neckar.lizergy.model.configuration.moduleLayout.planning.PvStringsPlanningModelInformation
import it.neckar.lizergy.model.configuration.moduleLayout.planning.toRoofStringsConfiguration
import it.neckar.lizergy.model.configuration.quote.builder.InverterConfiguration
import it.neckar.lizergy.model.configuration.quote.builder.ResolvedInverterConfiguration
import it.neckar.open.collections.fastForEach
import it.neckar.react.common.*
import it.neckar.react.common.form.*
import it.neckar.react.common.form.EditableStatus.*
import kotlinx.atomicfu.atomic
import react.*
import react.dom.*

/**
 * Creates a MeisterCharts module strings planning element
 */
fun RBuilder.moduleStringsPlanning(
  currentModuleLayout: ResolvedModuleLayout,
  stringsPlanningModel: PvStringsPlanningModelInformation,
  roofPlanningModel: PvRoofPlanningModelInformation,
  inverterConfigurations: List<ResolvedInverterConfiguration>,
  stringConfigurationToEdit: StateInstance<InverterConfiguration.StringConfiguration?>,
  editableStatus: EditableStatus,
  changeListener: (updatedModel: PvStringsPlanningModel) -> Unit,
): Unit = child(ModuleStringsPlanning) {
  attrs {
    this.currentModuleLayout = currentModuleLayout
    this.stringsPlanningModel = stringsPlanningModel
    this.roofPlanningModel = roofPlanningModel
    this.inverterConfigurations = inverterConfigurations
    this.stringConfigurationToEdit = stringConfigurationToEdit
    this.changeListener = changeListener
    this.editableStatus = editableStatus
  }
}

val ModuleStringsPlanning: FC<ModuleStringsPlanningProps> = fc("ModuleStringsPlanning") { props ->
  val currentModuleLayout = props::currentModuleLayout.safeGet()
  val stringsPlanningModel = props::stringsPlanningModel.safeGet()
  val roofPlanningModel = props::roofPlanningModel.safeGet()
  val inverterConfigurations = props::inverterConfigurations.getNotNull()
  val stringConfigurationToEdit = props::stringConfigurationToEdit.getNotNull()
  val changeListener = props::changeListener.safeGet()
  val editableStatus = props::editableStatus.safeGet()


  /**
   * The *mutable* model that is updated
   */
  val updatingMutableModelFromReact = useMemo {
    atomic(false)
  }

  val mutableRoofPlanningModel = useMemo {
    PvRoofPlanningModel().also { mutableModel ->
      //Apply the loaded model initially!
      roofPlanningModel.applyToMutableModel(mutableModel)
    }
  }

  val mutableStringsPlanningModel = useMemo { PvStringsPlanningModel() }

  useEffect(mutableStringsPlanningModel) {
    //Apply the loaded model initially!
    stringsPlanningModel.applyToMutableModel(mutableStringsPlanningModel, currentModuleLayout, mutableRoofPlanningModel.moduleAreas)
    val relevantModulesString = stringConfigurationToEdit.value?.modulesStrings?.firstOrNull { it.moduleLayoutId == currentModuleLayout.id }
    mutableStringsPlanningModel.set(relevantModulesString?.uuid)

    //Publish the changes
    val publishChanges: (newValue: Any?) -> Unit = {
      if (updatingMutableModelFromReact.value.not()) {
        changeListener(mutableStringsPlanningModel)
      }
    }

    mutableStringsPlanningModel.roofStringsConfiguration.roofStringsProperty.consume(action = publishChanges)
    mutableStringsPlanningModel.roofStringsConfiguration.roofStringsProperty.consumeImmediately { roofStrings ->
      roofStrings.fastForEach {
        it.modulesProperty.consume(action = publishChanges)
      }
    }
    mutableStringsPlanningModel.selectedStringProperty.consumeImmediately(action = publishChanges)
  }

  val stringsPlanningGestalt: PvStringsPlanningGestalt = useMemo {
    PvStringsPlanningGestalt(mutableStringsPlanningModel, mutableRoofPlanningModel).also {
      it.mode = when (editableStatus) {
        Editable -> PvRoofPlanningLayer.Mode.Planning
        ReadOnly -> PvRoofPlanningLayer.Mode.Rendering
      }
    }
  }

  //Apply the changes
  updatingMutableModelFromReact.value = true
  try {
    roofPlanningModel.applyToMutableModel(mutableRoofPlanningModel)

    mutableStringsPlanningModel.roofStringsConfiguration.set(inverterConfigurations.toRoofStringsConfiguration(currentModuleLayout, mutableRoofPlanningModel.moduleAreas))
    val relevantModulesString = stringConfigurationToEdit.value?.modulesStrings?.firstOrNull { it.moduleLayoutId == currentModuleLayout.id }
    stringsPlanningGestalt.pvStringsPlanningLayer.stringSelected(relevantModulesString?.uuid)

    stringsPlanningGestalt.mode = when (editableStatus) {
      Editable -> PvRoofPlanningLayer.Mode.Planning
      ReadOnly -> PvRoofPlanningLayer.Mode.Rendering
    }
  } finally {
    updatingMutableModelFromReact.value = false
  }


  div("row my-5") {
    meistercharts(
      description = "String Planner",
      gestalt = stringsPlanningGestalt,
      // Always mark as dirty on rerender. MeisterCharts is fast!
      callMarkAsDirty = true,
    )
  }

}


external interface ModuleStringsPlanningProps : Props {
  var currentModuleLayout: ResolvedModuleLayout
  var stringsPlanningModel: PvStringsPlanningModelInformation
  var roofPlanningModel: PvRoofPlanningModelInformation
  var changeListener: (updatedModel: PvStringsPlanningModel) -> Unit
  var inverterConfigurations: List<ResolvedInverterConfiguration>
  var stringConfigurationToEdit: StateInstance<InverterConfiguration.StringConfiguration?>
  var editableStatus: EditableStatus
}
